// src/pages/VocationalUniversity.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "../styles/VocationalUniversity.css";
import Navbar from "../components/Navbar"; // ✅ Navbar centralizada

const PAGE_SIZE = 5;
const LS_KEY = (userId) => `vocationalUniversityProgress:${userId}`;

const VocationalUniversity = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [responses, setResponses] = useState({});
  const [questions, setQuestions] = useState([]);
  const [userData, setUserData] = useState(null);
  const [showSidebar, setShowSidebar] = useState(false);
  const navigate = useNavigate();

  // 🔹 Guardar progreso en localStorage
  const persistLocal = (draft = {}) => {
    if (!userData?._id) return;
    const payload = {
      responses: draft.responses ?? responses,
      currentStep: draft.currentStep ?? currentStep,
      updatedAt: new Date().toISOString(),
      version: 1,
    };
    localStorage.setItem(LS_KEY(userData._id), JSON.stringify(payload));
  };

  // 🔹 Cargar usuario y preguntas
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/users/me`,
          { headers: { Authorization: token } }
        );
        setUserData(response.data);
      } catch (error) {
        console.error("Error al obtener usuario:", error);
      }
    };

    fetchUserData();

    const allQuestions = [
      // SECCIÓN 1: Intereses Generales
      {
        question: "1. ¿Qué tipo de libros prefieres leer?",
        options: [
          "a) Ciencias y tecnología",
          "b) Historia o filosofía",
          "c) Novelas o poesía",
          "d) Psicología o autoayuda"
        ]
      },
      {
        question: "2. ¿Qué clase disfrutas más en la escuela?",
        options: [
          "a) Matemáticas o física",
          "b) Historia o ciencias sociales",
          "c) Arte o música",
          "d) Biología o química"
        ]
      },
      {
        question: "3. ¿Te interesa aprender cómo funcionan los dispositivos tecnológicos?",
        options: [
          "a) Sí, me fascina comprender su funcionamiento",
          "b) Prefiero temas sociales",
          "c) Solo si tienen diseño",
          "d) Salud o cuerpo humano"
        ]
      },
      {
        question: "4. ¿Te gusta analizar y resolver problemas complejos?",
        options: [
          "a) Lógicos o matemáticos",
          "b) Sociales o históricos",
          "c) Creativos",
          "d) Relacionados con personas"
        ]
      },
      {
        question: "5. ¿Qué tipo de proyectos disfrutas más?",
        options: [
          "a) Experimentos o análisis de datos",
          "b) Investigaciones sociales",
          "c) Proyectos artísticos",
          "d) Proyectos productivos o de negocio"
        ]
      },
      {
        question: "6. ¿Te interesan los idiomas?",
        options: [
          "a) Textos técnicos",
          "b) Comunicación social",
          "c) Expresión creativa",
          "d) Ayudar a otros"
        ]
      },
      {
        question: "7. ¿Qué materias disfrutas más?",
        options: [
          "a) Matemáticas o computación",
          "b) Historia o geografía",
          "c) Arte o música",
          "d) Ciencias naturales"
        ]
      },
      {
        question: "8. ¿Qué tema prefieres investigar?",
        options: [
          "a) Tecnología",
          "b) Procesos sociales",
          "c) Arte",
          "d) Gestión organizacional"
        ]
      },
      {
        question: "9. ¿Te interesa trabajar en un laboratorio?",
        options: [
          "a) Científico",
          "b) Social",
          "c) Creativo",
          "d) Atención a personas"
        ]
      },
      {
        question: "10. ¿Qué haces en tu tiempo libre?",
        options: [
          "a) Resolver retos lógicos",
          "b) Leer o investigar",
          "c) Crear o dibujar",
          "d) Ayudar a otras personas"
        ]
      },
      // SECCIÓN 2: Habilidades y Preferencias Laborales
      {
        question: "11. ¿Qué prefieres al trabajar en equipo?",
        options: [
          "a) Organizar tareas",
          "b) Analizar información",
          "c) Proponer ideas",
          "d) Apoyar al grupo"
        ]
      },
      {
        question: "12. ¿Cómo enfrentas un problema?",
        options: [
          "a) Soluciones técnicas",
          "b) Análisis del contexto",
          "c) Ideas originales",
          "d) Tomar decisiones"
        ]
      },
      {
        question: "13. ¿Cuál es tu mayor fortaleza?",
        options: [
          "a) Lógica",
          "b) Análisis",
          "c) Creatividad",
          "d) Empatía"
        ]
      },
      {
        question: "14. ¿Qué tan cómodo te sientes con actividades prácticas?",
        options: [
          "a) Muy cómodo con equipos",
          "b) Prefiero planear",
          "c) Creativas",
          "d) Ayudan a otros"
        ]
      },
      {
        question: "15. ¿Cómo te describen tus amigos?",
        options: [
          "a) Analítico",
          "b) Reflexivo",
          "c) Creativo",
          "d) Solidario"
        ]
      },
      {
        question: "16. ¿Qué actividades disfrutas más?",
        options: [
          "a) Trabajar con datos",
          "b) Analizar textos",
          "c) Diseñar",
          "d) Coordinar personas"
        ]
      },
      {
        question: "17. ¿Cómo prefieres estudiar?",
        options: [
          "a) Ejercicios",
          "b) Lectura",
          "c) Esquemas visuales",
          "d) En grupo"
        ]
      },
      {
        question: "18. ¿Qué prefieres en un trabajo?",
        options: [
          "a) Procesos claros",
          "b) Aprendizaje",
          "c) Libertad creativa",
          "d) Dirección y liderazgo"
        ]
      },
      {
        question: "19. ¿Qué habilidad disfrutas desarrollar?",
        options: [
          "a) Técnica",
          "b) Analítica",
          "c) Artística",
          "d) Social"
        ]
      },
      // SECCIÓN 3: Aspiraciones y Valores
      {
        question: "20. ¿Cómo te ves en 10 años?",
        options: [
          "a) Innovando",
          "b) Investigando",
          "c) Creando",
          "d) Ayudando"
        ]
      },
      {
        question: "21. ¿Qué valoras más en un trabajo?",
        options: [
          "a) Innovación",
          "b) Conocimiento",
          "c) Expresión",
          "d) Resultados"
        ]
      },
      {
        question: "22. ¿Qué proyecto elegirías?",
        options: [
          "a) Eficiente",
          "b) Formativo",
          "c) Original",
          "d) Rentable"
        ]
      },
      {
        question: "23. ¿Cómo trabajas bajo presión?",
        options: [
          "a) Me enfoco",
          "b) Prefiero tiempo",
          "c) Creativo",
          "d) Organizo prioridades"
        ]
      },
      {
        question: "24. ¿Te interesa el área de la salud?",
        options: [
          "a) Desde tecnología",
          "b) Desde investigación",
          "c) No",
          "d) Desde atención directa"
        ]
      },
      {
        question: "25. ¿Qué impacto te gustaría tener?",
        options: [
          "a) Técnico",
          "b) Social",
          "c) Artístico",
          "d) Organizacional"
        ]
      },
      {
        question: "26. ¿Qué proyectos te emocionan más?",
        options: [
          "a) Tecnológicos",
          "b) Sociales",
          "c) Creativos",
          "d) De negocio"
        ]
      },
      {
        question: "27. ¿Qué prefieres al planear tu futuro?",
        options: [
          "a) Metas técnicas",
          "b) Explorar",
          "c) Pasión creativa",
          "d) Proyecto propio"
        ]
      },
      {
        question: "28. ¿Cómo imaginas tu ambiente laboral ideal?",
        options: [
          "a) Tecnológico",
          "b) Académico",
          "c) Creativo",
          "d) Empresarial"
        ]
      },
      {
        question: "29. ¿Qué haces en tu tiempo libre?",
        options: [
          "a) Aprender tecnología",
          "b) Leer",
          "c) Crear",
          "d) Actividades sociales"
        ]
      },
      {
        question: "30. ¿Qué rol prefieres en un proyecto?",
        options: [
          "a) Técnico",
          "b) Investigador",
          "c) Diseñador",
          "d) Coordinador"
        ]
      },
      // SECCIÓN 4: Estilos de Trabajo
      {
        question: "31. ¿Cómo prefieres trabajar?",
        options: [
          "a) Individual",
          "b) Reflexivo",
          "c) Creativo",
          "d) Liderando"
        ]
      },
      {
        question: "32. ¿Qué problemas te gusta resolver?",
        options: [
          "a) Técnicos",
          "b) Sociales",
          "c) Creativos",
          "d) Organizacionales"
        ]
      },
      {
        question: "33. ¿Qué te motiva al terminar un proyecto?",
        options: [
          "a) Funciona",
          "b) Aprendizaje",
          "c) Expresión",
          "d) Resultados"
        ]
      },
      {
        question: "34. ¿Qué rol asumes en equipo?",
        options: [
          "a) Especialista",
          "b) Analista",
          "c) Creativo",
          "d) Líder"
        ]
      },
      // SECCIÓN 5: Retos y Motivaciones
      {
        question: "35. ¿Cómo reaccionas ante un problema inesperado?",
        options: [
          "a) Solución técnica",
          "b) Analizo",
          "c) Improviso",
          "d) Decido rápido"
        ]
      },
      {
        question: "36. ¿Qué área te interesa más?",
        options: [
          "a) Ingeniería",
          "b) Ciencias sociales",
          "c) Artes",
          "d) Salud"
        ]
      },
      {
        question: "37. ¿Qué retos disfrutas más?",
        options: [
          "a) Lógicos",
          "b) Analíticos",
          "c) Creativos",
          "d) Estratégicos"
        ]
      },
      {
        question: "38. ¿Qué área necesita más apoyo?",
        options: [
          "a) Tecnología",
          "b) Educación",
          "c) Arte",
          "d) Economía"
        ]
      },
      {
        question: "39. ¿Qué eventos disfrutas más?",
        options: [
          "a) Congresos",
          "b) Seminarios",
          "c) Exposiciones",
          "d) Foros empresariales"
        ]
      },
      {
        question: "40. ¿Qué tareas te entusiasman más?",
        options: [
          "a) Resolver problemas",
          "b) Analizar",
          "c) Crear",
          "d) Dirigir"
        ]
      },
      // SECCIÓN 6: Decisiones y Futuro
      {
        question: "41. ¿Cómo prefieres aprender?",
        options: [
          "a) Practicando",
          "b) Investigando",
          "c) Experimentando",
          "d) Aplicando"
        ]
      },
      {
        question: "42. ¿Qué te genera mayor satisfacción?",
        options: [
          "a) Prototipo",
          "b) Análisis",
          "c) Creación",
          "d) Objetivos"
        ]
      },
      {
        question: "43. ¿Qué prefieres en una materia?",
        options: [
          "a) Aplicación",
          "b) Teoría",
          "c) Creatividad",
          "d) Casos reales"
        ]
      },
      {
        question: "44. ¿Qué buscas en una carrera?",
        options: [
          "a) Retos",
          "b) Desarrollo intelectual",
          "c) Expresión",
          "d) Liderazgo"
        ]
      },
      {
        question: "45. ¿Cómo resuelves conflictos?",
        options: [
          "a) Lógica",
          "b) Reflexión",
          "c) Creatividad",
          "d) Decisión"
        ]
      },
      {
        question: "46. ¿En qué te basas para decidir?",
        options: [
          "a) Datos",
          "b) Análisis",
          "c) Intuición",
          "d) Resultados"
        ]
      },
      {
        question: "47. ¿Qué habilidad quieres desarrollar?",
        options: [
          "a) Técnica",
          "b) Crítica",
          "c) Creativa",
          "d) Directiva"
        ]
      },
      {
        question: "48. ¿Qué proyectos te gustaría liderar?",
        options: [
          "a) Tecnológicos",
          "b) Educativos",
          "c) Artísticos",
          "d) Empresariales"
        ]
      },
      {
        question: "49. ¿Qué herramientas prefieres usar?",
        options: [
          "a) Tecnológicas",
          "b) Bibliográficas",
          "c) Creativas",
          "d) Gestión"
        ]
      },
      {
        question: "50. ¿Qué disfrutas más en equipo?",
        options: [
          "a) Resolver",
          "b) Debatir",
          "c) Crear",
          "d) Dirigir"
        ]
      },
      // SECCIÓN 7: Perfil Completo
      {
        question: "51. ¿Qué te motiva más en tu día a día?",
        options: [
          "a) Tecnología",
          "b) Comprender sociedad",
          "c) Crear",
          "d) Lograr metas"
        ]
      },
      {
        question: "52. ¿Cómo te describen?",
        options: [
          "a) Analítico",
          "b) Reflexivo",
          "c) Creativo",
          "d) Emprendedor"
        ]
      },
      {
        question: "53. ¿Qué valoras más en un trabajo futuro?",
        options: [
          "a) Innovación",
          "b) Aprendizaje",
          "c) Libertad",
          "d) Crecimiento económico"
        ]
      },
      {
        question: "54. ¿Qué te gustaría desarrollar?",
        options: [
          "a) Ingeniería",
          "b) Humanidades",
          "c) Arte",
          "d) Negocios"
        ]
      },
      {
        question: "55. ¿Cómo te gustaría tu vida profesional?",
        options: [
          "a) Tecnológica",
          "b) Intelectual",
          "c) Creativa",
          "d) Empresarial"
        ]
      },
      {
        question: "56. ¿Qué actividad te resulta más atractiva?",
        options: [
          "a) Resolver problemas técnicos",
          "b) Analizar fenómenos sociales",
          "c) Crear productos creativos",
          "d) Dirigir o emprender proyectos"
        ]
      },
      {
        question: "57. ¿Qué rol te gustaría asumir?",
        options: [
          "a) Especialista técnico",
          "b) Investigador",
          "c) Creativo",
          "d) Líder"
        ]
      },
      {
        question: "58. ¿Qué te motiva al pensar en tu futuro?",
        options: [
          "a) Innovar en tecnología",
          "b) Generar conocimiento",
          "c) Expresarte",
          "d) Crear empresa"
        ]
      },
      {
        question: "59. ¿Qué problemas disfrutas resolver?",
        options: [
          "a) Técnicos",
          "b) Sociales",
          "c) Creativos",
          "d) De gestión o mercado"
        ]
      },
      {
        question: "60. ¿Cómo te gustaría generar ingresos?",
        options: [
          "a) Ciencia/tecnología",
          "b) Docencia/investigación",
          "c) Creatividad/arte",
          "d) Negocios/empresa"
        ]
      }
    ];

    setQuestions(allQuestions);
  }, []);

  // 🔹 Recuperar progreso guardado
  useEffect(() => {
    if (!userData?._id) return;
    try {
      const raw = localStorage.getItem(LS_KEY(userData._id));
      if (raw) {
        const saved = JSON.parse(raw);
        if (saved?.responses) setResponses(saved.responses);
        if (Number.isInteger(saved?.currentStep)) setCurrentStep(saved.currentStep);
      }
    } catch (e) {
      console.warn("No se pudo cargar el progreso local:", e);
    }
  }, [userData?._id]);

  const steps = Math.ceil(questions.length / PAGE_SIZE);

  const handleChange = (e, index) => {
    const next = { ...responses, [index]: e.target.value };
    setResponses(next);
    persistLocal({ responses: next });
  };

  const handleNext = () => {
    if (validateStep()) {
      setCurrentStep((prev) => {
        const next = prev + 1;
        persistLocal({ currentStep: next });
        return next;
      });
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handlePrev = () => {
    setCurrentStep((prev) => {
      const next = Math.max(prev - 1, 0);
      persistLocal({ currentStep: next });
      return next;
    });
  };

  const validateStep = () => {
    const start = currentStep * PAGE_SIZE;
    const end = Math.min(start + PAGE_SIZE, questions.length);
    for (let i = start; i < end; i++) {
      if (!responses[i]) {
        alert("Por favor, responde todas las preguntas antes de continuar.");
        return false;
      }
    }
    return true;
  };

  const scoringMap = ['a', 'b', 'c', 'd'];

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("token");

    const scoredResponses = {};
    Object.keys(responses).forEach((key) => {
      const index = parseInt(responses[key]);
      scoredResponses[key] = scoringMap[index];
    });

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/users/submit-vocationalUniversity`,
        { responses: scoredResponses },
        { headers: { Authorization: token } }
      );

      if (response.status === 200) {
        alert("Cuestionario enviado con éxito");
        if (userData?._id) localStorage.removeItem(LS_KEY(userData._id));
        navigate("/dashboard");
      } else {
        alert("Hubo un problema al enviar el cuestionario");
      }
    } catch (error) {
      console.error("Error al enviar el cuestionario:", error);
      alert("Ocurrió un error. Intenta de nuevo.");
    }
  };

  const handleSaveAndExit = () => {
    persistLocal();
    alert("Progreso guardado localmente. Podrás continuar luego desde este equipo.");
    navigate("/dashboard");
  };

  const toggleSidebar = () => setShowSidebar(!showSidebar);
  const closeSidebar = () => setShowSidebar(false);

  return (
    <>
      <Navbar />

      <div className="dashboard-layout">
        <div className={`sidebar-overlay ${showSidebar ? "show" : ""}`} onClick={closeSidebar}></div>

        <div className={`dashboard-sidebar ${showSidebar ? "show" : ""}`}>
          <div className="sidebar-header">
            <h3>Orientación Vocacional para Nivel Superior</h3>
          </div>

          <div className="sidebar-menu">
            <div className="survey-progress">
              <div className="progress-info">
                <span>Progreso</span>
                <span>{currentStep + 1}/{steps}</span>
              </div>
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${((currentStep + 1) / steps) * 100}%` }}
                />
              </div>
            </div>
          </div>

          <button
            className="menu-item return-dashboard"
            onClick={() => {
              if (window.confirm("¿Estás seguro de que deseas volver al dashboard? Tus respuestas se han guardado y podrás continuar luego.")) {
                handleSaveAndExit();
              }
            }}
          >
            <i className="bi bi-arrow-left-circle"></i>
            Volver al Dashboard
          </button>
        </div>

        <div className="dashboard-main">
          <div className="survey-container">
            <div className="survey-header">
              <h2>Cuestionario de Orientación Vocacional para Nivel Superior</h2>
              <p className="survey-description">
                Responde las siguientes preguntas para conocer tus áreas de afinidad.
              </p>
              <div className="button-container">
                <button
                  type="button"
                  className="nav-button prev"
                  onClick={handleSaveAndExit}
                >
                  <i className="bi bi-floppy"></i>
                  Guardar y salir
                </button>
              </div>
            </div>

            <form className="survey-form" onSubmit={handleSubmit}>
              <div className="questions-group">
                {questions
                  .slice(currentStep * PAGE_SIZE, (currentStep + 1) * PAGE_SIZE)
                  .map((q, index) => {
                    const globalIndex = index + currentStep * PAGE_SIZE;
                    return (
                      <div className="question-card" key={globalIndex}>
                        <div className="question-header">
                          <span className="question-number">
                            Pregunta {globalIndex + 1}
                          </span>
                          <p className="question-text">{q.question}</p>
                        </div>

                        <div className="options-grid">
                          {q.options.map((option, i) => (
                            <label
                              key={i}
                              className={`option-card ${responses[globalIndex] === String(i) ? 'selected' : ''}`}
                            >
                              <input
                                type="radio"
                                name={`q${globalIndex}`}
                                value={i}
                                checked={responses[globalIndex] === String(i)}
                                onChange={(e) => handleChange(e, globalIndex)}
                                required
                              />
                              <span className="option-text">{option}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    );
                  })}
              </div>

              <div className="navigation-controls">
                {currentStep > 0 && (
                  <button type="button" className="nav-button prev" onClick={handlePrev}>
                    <i className="bi bi-arrow-left"></i>
                    Anterior
                  </button>
                )}

                {currentStep < steps - 1 ? (
                  <button type="button" className="nav-button next" onClick={handleNext}>
                    Siguiente <i className="bi bi-arrow-right"></i>
                  </button>
                ) : (
                  <button type="submit" className="nav-button submit">
                    Finalizar <i className="bi bi-check-circle"></i>
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default VocationalUniversity;
